export type MeResponse = {
  sessionIdPrefix: string
  documentCount: number
  /** Present when server links `@owlbert/core`; true if backend URL + token env are set. */
  hostedBackendConfigured?: boolean
  /** When hosted backend is configured and workspace resolution succeeded. */
  hostedReady?: boolean
  hostedWorkspaceId?: string
  hostedError?: { code: string; message: string }
}

export type DocumentStorageMode = 'session' | 'hosted'

export type FetchDocumentsResponse = {
  documents: UserDocumentRow[]
  storage?: DocumentStorageMode
}

export type CreateDocumentResponse = {
  document: UserDocumentRow
  storage?: DocumentStorageMode
}

export type UpdateDocumentResponse = {
  document: UserDocumentRow
  storage?: DocumentStorageMode
}

export type DeleteDocumentResponse = {
  ok: true
  storage?: DocumentStorageMode
}

export type HostedAssistantRow = {
  id: string
  name: string
  description: string
  /** System / developer instructions (cloud sync full row). */
  instructions?: string
  greeting: string
  llm: string
  tools: string[]
}

export type AssistantUpsertBody = {
  name: string
  description?: string
  instructions?: string
  greeting?: string
  llm?: string
  /** String (comma-separated) or string[]; server normalizes. */
  tools?: string[] | string
}

export type CreateAssistantResponse = {
  assistant: unknown
  storage: 'hosted'
}

export type UpdateAssistantResponse = {
  assistant: unknown
  storage: 'hosted'
}

export type FetchAssistantsResponse = {
  assistants: HostedAssistantRow[]
  storage: 'none' | 'hosted'
  error?: string
  message?: string
}

export type UserDocumentRow = {
  id: string
  name: string
  content: string
  contentHash: string
  chunkCount: number
  mimeType?: string | null
  sourceFilename?: string | null
  /** Original uploaded file size in bytes, when known. */
  sizeBytes?: number | null
  /** Base64-encoded original bytes (e.g. PDF) when stored by the hosted backend. */
  blob?: string | null
  /** Sanitized-on-render HTML preview (Word/Excel), when available. */
  previewHtml?: string | null
  /** True once a content-aware title has been generated for this doc (or attempted). */
  autoTitled?: boolean
  /** ISO timestamp of when the document was uploaded/created, when known. */
  uploadedAt?: string | null
  /** Free-form user note attached to the document. */
  comment?: string | null
}

export type UploadDocumentsResponse = {
  documents: UserDocumentRow[]
  failed: { name: string; error: string }[]
  storage?: DocumentStorageMode
}

async function parseJson<T>(r: Response): Promise<T> {
  if (!r.ok) {
    const text = await r.text().catch(() => '')
    throw new Error(text || `HTTP ${r.status}`)
  }
  return (await r.json()) as T
}

export async function fetchMe(): Promise<MeResponse> {
  const r = await fetch('/api/me', { credentials: 'include' })
  return parseJson<MeResponse>(r)
}

export async function fetchDocuments(): Promise<FetchDocumentsResponse> {
  const r = await fetch('/api/documents', { credentials: 'include' })
  return parseJson<FetchDocumentsResponse>(r)
}

export async function createDocument(input: { name: string; content: string }): Promise<CreateDocumentResponse> {
  const r = await fetch('/api/documents', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  })
  return parseJson<CreateDocumentResponse>(r)
}

export async function uploadDocuments(files: readonly File[]): Promise<UploadDocumentsResponse> {
  const form = new FormData()
  for (const f of files) {
    form.append('file', f, f.name)
  }
  const r = await fetch('/api/documents/upload', {
    method: 'POST',
    credentials: 'include',
    body: form,
  })
  return parseJson<UploadDocumentsResponse>(r)
}

export async function updateDocument(
  id: string,
  input: { name: string; content: string },
): Promise<UpdateDocumentResponse> {
  const r = await fetch(`/api/documents/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  })
  return parseJson<UpdateDocumentResponse>(r)
}

export async function deleteDocument(id: string): Promise<DeleteDocumentResponse> {
  const r = await fetch(`/api/documents/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
  })
  return parseJson<DeleteDocumentResponse>(r)
}

export async function fetchAssistants(): Promise<FetchAssistantsResponse> {
  const r = await fetch('/api/assistants', { credentials: 'include' })
  return parseJson<FetchAssistantsResponse>(r)
}

export async function createAssistant(body: AssistantUpsertBody): Promise<CreateAssistantResponse> {
  const r = await fetch('/api/assistants', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  return parseJson<CreateAssistantResponse>(r)
}

export async function updateAssistant(id: string, body: Partial<AssistantUpsertBody>): Promise<UpdateAssistantResponse> {
  const r = await fetch(`/api/assistants/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  return parseJson<UpdateAssistantResponse>(r)
}
